import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { msalInstance, loginRequest } from '../../auth/msalConfig';
import { adminAuthAPI } from '../../services/api';
import {
  LOGOUT_FLAG_KEY,
  clearAdminSession,
  clearMsalCache,
  clearLogoutFlag,
  markSsoPending,
  clearSsoPending,
} from '../../utils/adminSession';
import { getReachOutBaseUrl } from '../../utils/crossAppSso';
import loginHero from '../../assets/login-hero.png';
import './Auth.css';

/** Remove stale MSAL interaction state that causes "No reply address" errors. */
function clearMsalState() {
  clearMsalCache();
}

/** Drop one-time SSO params so a refresh cannot replay an expired handoff token. */
function stripLoginQueryParams(keys) {
  const url = new URL(window.location.href);
  let changed = false;
  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

const REDIRECT_URI = `${window.location.origin}/admin/login`;
const BRIDGE_ATTEMPTED_KEY = 'wizetok_reachout_bridge_attempted';

// Login hint: the dashboard passes ?login_hint=user@company.com when launching Wizetok
// so ssoSilent knows exactly which account to use — more reliable than guessing from session cookie alone.
const LOGIN_HINT = new URLSearchParams(window.location.search).get('login_hint') ?? undefined;

// A handoff token or login_hint means this page was launched deliberately from
// ReachOut (Open Wizetok click / explicit SSO) — worth a "Signing in…" placeholder.
// A cold, plain visit gets the login form immediately; the ReachOut-session check
// then runs invisibly behind it and only intervenes (redirects) on an actual hit.
const HAS_HANDOFF_TOKEN = Boolean(new URLSearchParams(window.location.search).get('handoff'));
const SHOW_INITIAL_LOADING = HAS_HANDOFF_TOKEN || Boolean(LOGIN_HINT);

/**
 * Check for a ReachOut session via a hidden iframe + postMessage instead of a
 * visible window.location.replace() bounce — see CrossAppWizetokBridge.js's
 * `framed` handling on the ReachOut side, which this pairs with. Resolves with
 * the bridge's result, or null on a miss/timeout, and never navigates itself.
 */
function checkReachOutSessionSilently(bridgeUrl, expectedOrigin, timeoutMs = 4000) {
  return new Promise((resolve) => {
    let settled = false;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = bridgeUrl;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeoutId);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const onMessage = (event) => {
      if (settled) return;
      if (event.origin !== expectedOrigin) return;
      if (event.data?.type !== 'wizetok-bridge-result') return;
      settled = true;
      cleanup();
      resolve(event.data);
    };

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(null);
    }, timeoutMs);

    window.addEventListener('message', onMessage);
    document.body.appendChild(iframe);
  });
}
const MicrosoftMark = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const WizetokMark = ({ size = 36 }) => (
  <span className="wiz-mark" style={{ width: size, height: size }} aria-hidden="true">
    <svg viewBox="0 0 32 32" width={size * 0.55} height={size * 0.55}>
      <rect x="4" y="14" width="6" height="12" rx="1.2" fill="currentColor" />
      <rect x="13" y="8" width="6" height="18" rx="1.2" fill="currentColor" />
      <rect x="22" y="4" width="6" height="22" rx="1.2" fill="currentColor" />
    </svg>
  </span>
);

const AdminLogin = () => {
  const [loading, setLoading] = useState(SHOW_INITIAL_LOADING);
  const [error, setError] = useState('');
  const [method, setMethod] = useState('microsoft');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const _handleLoginResult = async (result) => {
    try {
      clearAdminSession();
      localStorage.setItem('token', result.idToken);
      const meResponse = await adminAuthAPI.getCurrentAdmin();
      clearLogoutFlag();
      stripLoginQueryParams(['handoff', 'skip_bridge', 'login_hint', 'from']);
      localStorage.setItem('admin', JSON.stringify(meResponse.data));
      navigate('/admin/dashboard');
    } catch (err) {
      localStorage.removeItem('token');
      localStorage.removeItem('admin');
      clearMsalCache();
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const finishLocalLogin = (data) => {
    clearMsalCache();
    clearAdminSession();
    clearLogoutFlag();
    stripLoginQueryParams(['handoff', 'skip_bridge', 'login_hint', 'from']);
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('admin', JSON.stringify(data.admin));
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const handoffToken = searchParams.get('handoff') || undefined;
      const fromReachOut = searchParams.get('from') === 'reachout';
      const skipBridge = searchParams.get('skip_bridge') === '1' || fromReachOut;

      // Only auto-login from an explicit ReachOut handoff (current ReachOut user).
      // Do not silent-SSO a cached Microsoft account or bounce to ReachOut —
      // that is why switching users kept restoring the previous person.
      if (handoffToken) {
        sessionStorage.setItem(BRIDGE_ATTEMPTED_KEY, '1');
        try {
          const response = await adminAuthAPI.consumeHandoff(handoffToken, 'reachout');
          if (!cancelled) {
            stripLoginQueryParams(['handoff', 'skip_bridge', 'login_hint', 'from']);
            finishLocalLogin(response.data);
          }
          return;
        } catch {
          stripLoginQueryParams(['handoff', 'skip_bridge', 'from']);
          if (cancelled) return;
        }
      }

      await msalInstance.initialize();
      if (cancelled) return;

      // Cross-app launch with login_hint should SSO even after an earlier logout.
      if (LOGIN_HINT) {
        sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      }

      // After explicit logout (without a fresh login_hint), do not silent-SSO / bridge back in.
      if (sessionStorage.getItem(LOGOUT_FLAG_KEY)) {
        sessionStorage.removeItem('wizetok_sso_redirect');
        sessionStorage.removeItem(BRIDGE_ATTEMPTED_KEY);
        if (!cancelled) setLoading(false);
        return;
      }

      // If already logged into ReachOut, exchange and stay on Wizetok.
      // Bridge uses source=wizetok so a miss returns here (never leaves you on ReachOut).
      // Only runs when a ReachOut URL is configured (APP_CONFIG.REACHOUT_URL /
      // REACT_APP_REACHOUT_URL); otherwise fall through to the login form.
      const reachOutBase = getReachOutBaseUrl();
      if (reachOutBase && !LOGIN_HINT && !skipBridge && !sessionStorage.getItem(BRIDGE_ATTEMPTED_KEY)) {
        sessionStorage.setItem(BRIDGE_ATTEMPTED_KEY, '1');
        const bridgeResult = await checkReachOutSessionSilently(
          `${reachOutBase}/sso/wizetok?source=wizetok`,
          reachOutBase
        );
        if (cancelled) return;
        if (bridgeResult?.ok && bridgeResult.redirectUrl) {
          setLoading(true);
          window.location.replace(bridgeResult.redirectUrl);
          return;
        }
        // No ReachOut session (or the check timed out) — fall through to the login form below.
      } else {
        sessionStorage.setItem(BRIDGE_ATTEMPTED_KEY, '1');
        if (skipBridge) {
          stripLoginQueryParams(['skip_bridge', 'from']);
        }
      }

      // ── Step 1: Handle return from Azure AD redirect ───────────────────────
      let redirectResult;
      try {
        redirectResult = await msalInstance.handleRedirectPromise();
      } catch {
        sessionStorage.removeItem('wizetok_sso_redirect');
        if (!cancelled) setLoading(false);
        return;
      }
      if (redirectResult) {
        sessionStorage.removeItem('wizetok_sso_redirect');
        clearSsoPending();
        await _handleLoginResult(redirectResult);
        return;
      }

      sessionStorage.removeItem('wizetok_sso_redirect');
      if (!cancelled) setLoading(false);
    };

    run().catch(() => {
      sessionStorage.removeItem('wizetok_sso_redirect');
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectMethod = (next) => {
    setMethod(next);
    setError('');
    if (next !== 'otp') {
      setOtpSent(false);
      setOtp('');
    }
  };

  const handleMicrosoftLogin = async () => {
    setSubmitting(true);
    setLoading(true);
    setError('');
    clearAdminSession();
    clearMsalState();
    markSsoPending();
    try {
      await msalInstance.initialize();
      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectUri: REDIRECT_URI,
        prompt: 'select_account',
      });
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await adminAuthAPI.passwordLogin(email.trim(), password);
      finishLocalLogin(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await adminAuthAPI.requestOtp(email.trim());
      setOtp('');
      setOtpSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await adminAuthAPI.verifyOtp(email.trim(), otp.trim());
      finishLocalLogin(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid or expired OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const cardInner = loading ? (
    <div className="wiz-login-quiet">Signing in…</div>
  ) : (
    <>
      <div className="wiz-method-tabs" role="tablist" aria-label="Sign-in method">
        <button
          type="button"
          role="tab"
          aria-selected={method === 'microsoft'}
          className={`wiz-method-tab${method === 'microsoft' ? ' is-active' : ''}`}
          onClick={() => selectMethod('microsoft')}
        >
          <MicrosoftMark size={14} />
          Microsoft
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={method === 'password'}
          className={`wiz-method-tab${method === 'password' ? ' is-active' : ''}`}
          onClick={() => selectMethod('password')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
          Password
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={method === 'otp'}
          className={`wiz-method-tab${method === 'otp' ? ' is-active' : ''}`}
          onClick={() => selectMethod('otp')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 3l7 3v6c0 5-3.5 8.5-7 9.5C8.5 20.5 5 17 5 12V6l7-3z" />
            <path d="M9.5 12.5l1.8 1.8 3.7-3.8" />
          </svg>
          OTP
        </button>
      </div>

      {error && <div className="wiz-error">{error}</div>}

      {method === 'microsoft' && (
        <div className="wiz-pane">
          <p className="wiz-pane-copy">Sign in with your Microsoft / company account.</p>
          <button
            type="button"
            className="wiz-primary-btn"
            onClick={handleMicrosoftLogin}
            disabled={submitting}
          >
            <MicrosoftMark size={16} />
            Sign in with Microsoft
          </button>
        </div>
      )}

      {method === 'password' && (
        <form className="wiz-pane" onSubmit={handlePasswordLogin}>
          <label className="wiz-label" htmlFor="admin-email">Email Address</label>
          <input
            id="admin-email"
            type="email"
            className="wiz-input"
            autoComplete="username"
            placeholder="Enter your work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label className="wiz-label" htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            type="password"
            className="wiz-input"
            autoComplete="current-password"
            placeholder="Enter your Microsoft / company password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="wiz-primary-btn" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      {method === 'otp' && !otpSent && (
        <form className="wiz-pane" onSubmit={handleSendOtp}>
          <label className="wiz-label" htmlFor="otp-email">Email Address</label>
          <input
            id="otp-email"
            type="email"
            className="wiz-input"
            autoComplete="username"
            placeholder="Enter your work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="wiz-primary-btn" disabled={submitting}>
            {submitting ? 'Sending OTP…' : 'Send OTP'}
          </button>
        </form>
      )}

      {method === 'otp' && otpSent && (
        <form className="wiz-pane" onSubmit={handleVerifyOtp}>
          <p className="wiz-pane-copy">Enter the OTP sent to {email}</p>
          <label className="wiz-label" htmlFor="otp-code">OTP Code</label>
          <input
            id="otp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="wiz-input wiz-input-otp"
            placeholder="Enter 6-digit OTP"
            maxLength={8}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\s/g, ''))}
            required
          />
          <button type="submit" className="wiz-primary-btn" disabled={submitting || otp.trim().length < 4}>
            {submitting ? 'Verifying…' : 'Verify OTP'}
          </button>
          <button
            type="button"
            className="wiz-text-btn"
            onClick={handleSendOtp}
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Resend OTP'}
          </button>
          <button
            type="button"
            className="wiz-text-btn"
            onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
          >
            Use a different email
          </button>
        </form>
      )}

      <div className="wiz-secure">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        Secure login powered by your organization.
      </div>
    </>
  );

  return (
    <div className="wiz-login-page">
      <div className="wiz-login-left">
        <div className="wiz-login-card">
          <div className="wiz-login-stack">
            <div className="wiz-card-header">
              <WizetokMark size={64} />
              <h1>Wizetok</h1>
              <p>AI Interview &amp; Assessment Platform</p>
            </div>
            {cardInner}
          </div>
        </div>
      </div>

      <div className="wiz-login-right">
        <img src={loginHero} alt="AI interview workspace" className="wiz-hero-photo" />
      </div>
    </div>
  );
};

export default AdminLogin;
